import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { apiRequest } from '../authConfig';

interface User {
    id: string;
    email: string;
    displayName: string;
    firstName: string;
    lastName: string;
    role: 'admin' | 'consultant';
    /**
     * Le compte Claude de cet utilisateur est-il connecté ?
     *
     * Rendu dès l'ouverture de session : sans jeton, aucune proposition ne peut
     * être générée, et le dire à l'entrée vaut mieux que de le découvrir au
     * moment de générer.
     */
    claudeConnected?: boolean;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    authError: string | null;
    login: () => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || null);
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);
    const retriedInteractively = useRef(false);

    useEffect(() => {
        if (account) {
            authenticateUser();
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account]);

    const authenticateUser = async () => {
        try {
            setIsLoading(true);
            setAuthError(null);

            const response = await instance.acquireTokenSilent({ ...apiRequest, account: account! });

            const backendResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ azureToken: response.accessToken }),
            });

            const contentType = backendResponse.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error("Réponse inattendue du serveur (le backend est-il joignable ?)");
            }

            const data = await backendResponse.json();
            if (!backendResponse.ok) {
                throw new Error(data?.error?.message || 'Authentification refusée par le serveur');
            }

            setToken(data.data.token);
            setUser(data.data.user);
            retriedInteractively.current = false;
        } catch (error: any) {
            console.error('Authentication error:', error);
            // Une seule tentative interactive de secours, pour éviter une boucle
            // de popups si le backend est en échec de façon persistante.
            if (!retriedInteractively.current) {
                retriedInteractively.current = true;
                try {
                    await instance.acquireTokenPopup(apiRequest);
                    await authenticateUser();
                    return;
                } catch {
                    // tombe dans le cas d'erreur ci-dessous
                }
            }
            setUser(null);
            setToken(null);
            setAuthError("Impossible de vous connecter. Réessayez ou contactez un administrateur.");
        } finally {
            setIsLoading(false);
        }
    };

    const login = async () => {
        try {
            await instance.loginPopup(loginScopes());
        } catch (error) {
            console.error('Login error:', error);
        }
    };

    const loginScopes = () => ({ scopes: ['openid', 'profile', 'email'] });

    const logout = () => {
        instance.logoutPopup();
        setUser(null);
        setToken(null);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                isLoading,
                isAuthenticated: !!user && !!token,
                authError,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
